import logger from '../utils/logger';
// Subscription Service
// Handles payment gateway configuration, plan information, and subscription operations

const API_BASE_URL = '/api';

export const getPaymentConfig = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/config`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch payment config: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    logger.error('Error fetching payment config:', error);
    throw error;
  }
};

/**
 * Get current user's plan and usage statistics
 */
export const getPlanUsage = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/plans/usage`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch plan usage: ${response.status}`);
    }

    const result = await response.json();
    // API returns { success: true, data: {...}, message: "..." }
    // Extract the data object
    return result.data;
  } catch (error) {
    logger.error('Error fetching plan usage:', error);
    throw error;
  }
};

/**
 * Get available plans with features
 */
export const getAvailablePlans = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/plans`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch plans: ${response.status}`);
    }

    const result = await response.json();
    // API returns { success: true, data: [...], message: "..." }
    // Extract the data array
    return result.data || [];
  } catch (error) {
    logger.error('Error fetching plans:', error);
    throw error;
  }
};

/**
 * Create Razorpay subscription order (for India)
 */
export const createRazorpaySubscription = async (planName) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/create-subscription`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ plan: planName }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create subscription');
    }

    return await response.json();
  } catch (error) {
    logger.error('Error creating Razorpay subscription:', error);
    throw error;
  }
};

/**
 * Verify Razorpay payment
 */
export const verifyRazorpayPayment = async (paymentData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Payment verification failed');
    }

    return await response.json();
  } catch (error) {
    logger.error('Error verifying payment:', error);
    throw error;
  }
};

/**
 * Generate Paddle payment link (for International)
 */
export const generatePaddlePayLink = async (planName) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/paddle/generate-pay-link`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ plan: planName }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to generate payment link');
    }

    return await response.json();
  } catch (error) {
    logger.error('Error generating Paddle payment link:', error);
    throw error;
  }
};

/**
 * Cancel current subscription
 */
export const cancelSubscription = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/cancel-subscription`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to cancel subscription');
    }

    return await response.json();
  } catch (error) {
    logger.error('Error cancelling subscription:', error);
    throw error;
  }
};

/**
 * Calculate days remaining in trial
 */
export const calculateTrialDaysRemaining = (planExpiresAt) => {
  if (!planExpiresAt) return 0;

  const expiryDate = new Date(planExpiresAt);
  const now = new Date();
  const diffTime = expiryDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

/**
 * Format currency based on gateway
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (currency === 'INR') {
    // Razorpay sends amount in paise (smallest unit)
    const rupees = amount / 100;
    return `₹${rupees.toLocaleString('en-IN')}`;
  } else {
    // USD
    return `$${amount}`;
  }
};
